import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * HAIRNSHAPE cart bridge.
 *
 * The custom "buy" sections (hs3-buy-luxe, hs-huile, hs-ser-buy) add to cart with their own
 * bespoke pack/upsell UI instead of Horizon's native <product-form-component>. This module lets
 * them plug into the theme's native cart-drawer architecture (open on add, live item sync,
 * header badge) by dispatching the same CartLinesUpdateEvent the native product form uses,
 * exactly the way assets/product-form.js does it.
 *
 * It also adds small, HAIRNSHAPE-specific extras to the drawer: keeping the custom header's
 * cart badge in sync (since that header is hand-built rather than Horizon's header-actions
 * component), a quick-add handler for the "you might also like" cards, and repositioning the
 * compare-at price under the line price. The "you might also like" cards themselves, and the
 * compare-at price values, are rendered entirely server-side by cart-drawer.liquid as part of
 * the same section payload as the rest of the cart — so they stay correct and in sync with zero
 * extra network calls or client-side timing guesses.
 */

var SECTION_ID = 'cart-drawer-section';

function updateHeaderBadge(count) {
  if (typeof count !== 'number') return;
  document.querySelectorAll('[data-hs-cart-count]').forEach(function (el) {
    el.textContent = String(count);
    el.hidden = count === 0;
  });
}

/* Lit le nombre d'articles depuis le HTML de section deja recu (data-hs-item-count, rendu par
   cart-drawer.liquid), pour eviter d'attendre un second appel reseau juste pour le badge. */
function parseItemCountFromSections(sections) {
  var html = sections && sections[SECTION_ID];
  if (!html) return null;
  var match = html.match(/data-hs-item-count[^>]*>\s*(\d+)\s*</);
  return match ? parseInt(match[1], 10) : null;
}

function refreshCart() {
  return fetch(Theme.routes.cart_url + '.json', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then(function (response) {
    if (!response.ok) throw new Error('Failed to fetch cart: ' + response.status);
    return response.json();
  });
}

/**
 * Adds one or more variants to the cart and notifies the rest of the theme about it, the same
 * way Horizon's own product-form does. Resolves once the cart drawer has the fresh cart data.
 * @param {Array<{id: number|string, quantity?: number, selling_plan?: number|string}>} items
 * @returns {Promise<any>}
 */
function addItems(items) {
  var deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map(function (it) {
        return { merchandiseId: String(it.id), quantity: it.quantity || 1 };
      }),
      promise: deferred.promise,
    })
  );

  var payload = {
    items: items.map(function (it) {
      var out = { id: Number(it.id), quantity: it.quantity || 1 };
      if (it.selling_plan) out.selling_plan = Number(it.selling_plan);
      return out;
    }),
    sections: SECTION_ID,
  };

  return fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  })
    .then(function (response) {
      return response.json();
    })
    .then(function (response) {
      if (response.status) {
        var err = new Error(response.message || 'Add to cart failed');
        document.dispatchEvent(new CartErrorEvent({ error: err.message, code: 'INVALID' }));

        return refreshCart().then(function (ajaxCart) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: {
              didError: true,
              items: ajaxCart.items,
              source: 'hs-cart-bridge',
              sourceId: 'hs-cart-bridge',
              itemCount: ajaxCart.item_count,
            },
          });
          throw err;
        });
      }

      /* Met a jour le badge du header tout de suite, a partir du HTML deja recu dans cette
         meme reponse (sections=cart-drawer-section), sans attendre le second aller-retour
         reseau ci-dessous. C'est ce second appel (refreshCart) qui etait responsable du gros
         temps d'attente ressenti avant que le panier ne s'incremente. */
      var earlyCount = parseItemCountFromSections(response.sections);
      if (earlyCount !== null) updateHeaderBadge(earlyCount);

      return refreshCart().then(function (ajaxCart) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'hs-cart-bridge',
            sourceId: 'hs-cart-bridge',
            itemCount: ajaxCart.item_count,
            sections: response.sections,
            didError: false,
          },
        });
        return ajaxCart;
      });
    })
    .catch(function (error) {
      deferred.reject(error);
      document.dispatchEvent(
        new CartErrorEvent({
          error: (error && error.message) || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      throw error;
    });
}

window.HSCart = { addItems: addItems };

/* Ouvre le tiroir panier depuis l'icone panier du header (au lieu de naviguer vers /cart). */
document.addEventListener('click', function (event) {
  var trigger = event.target.closest && event.target.closest('[data-hs-cart-open]');
  if (!trigger) return;
  var drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    event.preventDefault();
    drawer.open();
  }
});

/* HAIRNSHAPE : "cache misere" — floute/assombrit le reste de la page tant que le panier est
 * ouvert, pour masquer visuellement le temps d'aller-retour reseau entre le clic "ajouter" et
 * l'affichage du contenu a jour du panier.
 *
 * theme-drawer.js (natif) gere deja un vrai ::backdrop + fermeture au clic en mode modal
 * (<990px, showModal) ; voir la regle ::backdrop dans le <style> de cart-drawer.liquid pour le
 * flou ajoute a ce backdrop natif. En mode "squeeze" (>=990px, show()) il n'existe en revanche
 * aucun backdrop du tout — cet element (#hs-cart-backdrop, toujours present dans le DOM, invisible
 * par defaut) fournit le flou et la fermeture au clic dans ce mode. On le pilote simplement sur
 * les evenements theme-drawer:open / theme-drawer:close (natifs, bulles), qui se declenchent dans
 * les deux modes — en mode modal le natif prend deja le relais visuellement/au clic, donc le
 * montrer aussi ne cause aucun conflit (il reste sous le vrai backdrop, dans le "top layer").
 */
(function () {
  var backdrop = document.querySelector('[data-hs-cart-backdrop]');
  if (!backdrop) return;

  function setVisible(visible) {
    backdrop.classList.toggle('hs-cart-backdrop--visible', visible);
  }

  document.addEventListener('theme-drawer:open', function (event) {
    if (event.target && event.target.id === 'cart-drawer') setVisible(true);
  });

  document.addEventListener('theme-drawer:close', function (event) {
    if (event.target && event.target.id === 'cart-drawer') setVisible(false);
  });

  /* Si le panier est restaure deja ouvert au chargement de la page (sessionStorage), aucun
     evenement theme-drawer:open n'est emis (voir #onRestore dans theme-drawer.js) : on verifie
     donc aussi l'etat initial directement. */
  var initialDrawer = document.getElementById('cart-drawer');
  if (initialDrawer && initialDrawer.hasAttribute('open')) setVisible(true);

  /* Un clic n'importe ou sur la zone floutee referme le panier — sauf si une boite de dialogue
     imbriquee (ex: un modal de confirmation) est ouverte au-dessus, pour rester coherent avec le
     comportement natif du clic sur le backdrop en mode modal. */
  backdrop.addEventListener('click', function () {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer || typeof drawer.close !== 'function') return;
    var panel = drawer.querySelector('dialog');
    var nestedOpenDialog = panel && panel.querySelector('dialog[open]');
    if (nestedOpenDialog) return;
    drawer.close();
  });
})();

/* HAIRNSHAPE : prix barré sous le prix de chaque ligne.
 *
 * La valeur (déjà formatée, déjà multipliée par la quantité) est calculée côté serveur par
 * cart-drawer.liquid dans le JSON data-hs-compare-prices, qui fait partie du même rendu de
 * section que le reste du panier — donc toujours à jour après un morph natif, sans recalcul
 * ni appel réseau ici. On se contente de déplacer visuellement la valeur sous le prix en gras.
 */
function getComparePriceMap() {
  var el = document.querySelector('[data-hs-compare-prices]');
  if (!el) return {};
  try {
    return JSON.parse(el.textContent) || {};
  } catch (e) {
    return {};
  }
}

function relocateComparePrices(root) {
  var scope = root || document;
  var map = getComparePriceMap();
  scope.querySelectorAll('.cart-drawer__items .cart-items__table-row[data-key]').forEach(function (row) {
    var priceCell = row.querySelector('.cart-items__price');
    if (!priceCell) return;
    var value = map[row.getAttribute('data-key')];
    var existing = priceCell.querySelector('.hs-compare-price');
    if (value) {
      if (!existing) {
        existing = document.createElement('s');
        existing.className = 'hs-compare-price';
        priceCell.appendChild(existing);
      }
      if (existing.textContent !== value) existing.textContent = value;
    } else if (existing) {
      existing.remove();
    }
  });
}

relocateComparePrices();

/* Le tiroir morph seulement cart-items-component a chaque mise a jour du panier (Section
   Rendering API) : on observe ce remplacement pour replacer le prix barre sans dependre d'un
   delai arbitraire (evite tout decalage visuel). Travail 100% local au DOM, aucun appel reseau,
   donc negligeable en performance meme declenche souvent. */
var hsCartDrawerEl = document.getElementById('cart-drawer');
if (hsCartDrawerEl && window.MutationObserver) {
  var hsRelocateScheduled = false;
  var hsPriceObserver = new MutationObserver(function () {
    if (hsRelocateScheduled) return;
    hsRelocateScheduled = true;
    requestAnimationFrame(function () {
      hsRelocateScheduled = false;
      relocateComparePrices();
    });
  });
  hsPriceObserver.observe(hsCartDrawerEl, { childList: true, subtree: true });
}

/* Ajout rapide depuis une carte "vous aimerez aussi" (rendue cote serveur dans cart-drawer.liquid). */
document.addEventListener('click', function (event) {
  var btn = event.target.closest && event.target.closest('[data-hs-upsell-add]');
  if (!btn) return;
  event.preventDefault();
  if (btn.disabled || !window.HSCart || !window.HSCart.addItems) return;
  var variantId = btn.getAttribute('data-hs-upsell-add');
  btn.disabled = true;
  window.HSCart.addItems([{ id: Number(variantId), quantity: 1 }])
    .catch(function () {})
    .then(function () {
      btn.disabled = false;
    });
});

document.addEventListener(StandardEvents.cartLinesUpdate, function (event) {
  if (!event.promise) return;

  event.promise
    .then(function (result) {
      var detail = result && result.detail;
      if (detail && typeof detail.itemCount === 'number') updateHeaderBadge(detail.itemCount);
    })
    .catch(function (error) {
      if (error && error.name !== 'AbortError') {
        console.warn('[hs-cart-bridge] Event promise rejected:', error);
      }
    });
});
